import {
  forwardRef,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '@heidi/utils';

import { useDesignSystemIntl } from '../hooks/use-design-system-intl';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../tooltip';

import { FileCardDeleteButton } from './file-card-delete-button';
import { FileCardError } from './file-card-error';
import { FileCardLoading } from './file-card-loading';
import { getFileIcon, hasValidFileExtension } from './file-card-utils';

export interface FileCardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * The variant of the card
   * @default "default"
   */
  variant?: 'default' | 'loading' | 'error';
  /**
   * The size of the card
   * @default "default"
   */
  size?: 'default' | 'mobile';
  /**
   * The name of the file to display
   * Required when variant is "default" or "error", optional when variant is "loading"
   */
  fileName?: string;
  /**
   * The file type/extension (e.g., "PDF", "DOCX")
   * Displayed beneath the file name.
   */
  fileType?: string;
  /**
   * Optional custom content rendered inside the thumbnail container.
   * Useful for passing an <Image /> component or any visual preview.
   */
  thumbnail?: ReactNode;
  /**
   * Callback when the card is clicked
   */
  onClick?: () => void;
  /**
   * Callback when the delete button is clicked
   * When provided, a delete button will be displayed
   */
  onDelete?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Callback when the retry button is clicked (shown on error variant)
   */
  onRetry?: (e: MouseEvent<HTMLButtonElement>) => void;
  /**
   * Loading progress percentage (0-100)
   * Only used when variant is "loading"
   * When provided, shows a circular progress indicator instead of a spinning loader
   */
  loadingProgress?: number;
  /**
   * Error message to display in tooltip when variant is "error"
   */
  errorMessage?: string;
  /**
   * Label for the failed status text in error variant (default: "Failed")
   */
  failedLabel?: string;
  /**
   * Fallback tooltip text when no errorMessage is provided (default: "Upload failed")
   */
  fallbackErrorMessage?: string;
  /**
   * Optional test ID for testing purposes
   */
  'data-testid'?: string;
}

/**
 * FileCard component for displaying file information
 *
 * @example Basic usage
 * ```tsx
 * <FileCard
 *   fileName="Mental Health Care plan"
 *   fileType="PDF"
 *   onClick={() => handleFileClick()}
 * />
 * ```
 *
 * @example Loading state
 * ```tsx
 * <FileCard variant="loading" />
 * ```
 *
 * @example With custom thumbnail component
 * ```tsx
 * <FileCard
 *   fileName="X-Ray Scan"
 *   fileType="JPG"
 *   thumbnail={
 *     <img
 *       src="/images/xray-thumbnail.jpg"
 *       alt="X-Ray Scan"
 *       className="size-full object-cover"
 *     />
 *   }
 * />
 * ```
 */

const FileCard = forwardRef<HTMLDivElement, FileCardProps>(
  (
    {
      variant = 'default',
      size = 'default',
      fileName,
      fileType,
      thumbnail,
      onClick,
      onDelete,
      onRetry,
      loadingProgress,
      errorMessage,
      failedLabel,
      fallbackErrorMessage,
      className,
      onKeyDown,
      ...props
    },
    ref
  ) => {
    const intl = useDesignSystemIntl();
    const fileNameRef = useRef<HTMLParagraphElement>(null);
    const [isFileNameTruncated, setIsFileNameTruncated] = useState(false);
    const displayFileType = fileType || 'Unknown';
    const isInteractive = Boolean(onClick);

    // If the file has a valid extension, we hide it (truncate shows "filename...")
    // If not a valid extension, show the full name including any dots
    const shouldHideExtension = hasValidFileExtension(fileName || '');

    // Check if filename is truncated whenever fileName changes or container resizes
    useLayoutEffect(() => {
      const checkTruncation = () => {
        if (fileNameRef.current) {
          const isTruncated =
            fileNameRef.current.scrollWidth > fileNameRef.current.clientWidth;
          setIsFileNameTruncated(isTruncated);
        }
      };

      checkTruncation();

      const resizeObserver = new ResizeObserver(checkTruncation);
      if (fileNameRef.current) {
        resizeObserver.observe(fileNameRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [fileName]);

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || !onClick) {
        return;
      }

      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    };

    const interactiveProps = isInteractive
      ? {
          onClick,
          role: 'button' as const,
          tabIndex: 0,
          onKeyDown: handleKeyDown,
        }
      : {
          onKeyDown,
        };

    const renderThumbnail = () => {
      if (thumbnail) {
        return thumbnail;
      }

      const IconComponent = getFileIcon(fileType);

      return (
        <IconComponent
          className={cn('text-text-secondary', iconClasses)}
          data-testid="file-card-default-icon"
          aria-hidden="true"
        />
      );
    };
    // Size-specific classes
    const isMobileSize = size === 'mobile';
    const cardClasses = isMobileSize
      ? 'h-auto w-[204px] gap-2 rounded-xl py-1.5 ps-1.5 pe-2.5'
      : 'h-10 w-44 gap-2.5 rounded-lg py-1 ps-1 pe-2';
    const iconContainerClasses = isMobileSize
      ? 'size-10 rounded-lg'
      : 'size-8 rounded-md';
    const iconClasses = isMobileSize ? 'size-6' : 'size-4';
    const textContainerClasses = isMobileSize
      ? 'gap-2 justify-center pt-0'
      : 'gap-0 pt-0.5';
    const fileNameClasses = isMobileSize
      ? 'text-sm leading-none'
      : 'text-xs leading-tight';
    const fileTypeClasses = isMobileSize
      ? 'text-xs leading-none'
      : 'text-[10px] leading-tight';

    // Render loading state (extracted to FileCardLoading for max-lines compliance)
    // Name the delete control after the card it belongs to. Several cards sit
    // side by side, so the default "Delete file" makes every one of them the
    // same control to anyone not looking at the screen. The visible name is
    // ambiguous for the same reason a screen reader is: it is the only thing
    // distinguishing the buttons, and it is not on the button.
    const deleteLabel = fileName
      ? intl.formatMessage(
          {
            defaultMessage: 'Delete {fileName}',
            id: 'cxqfdEdNTH',
            description:
              'Accessible label for the FileCard delete button, naming the file it removes',
          },
          { fileName }
        )
      : undefined;

    if (variant === 'loading') {
      return (
        <FileCardLoading
          ref={ref}
          size={size}
          loadingProgress={loadingProgress}
          onDelete={onDelete}
          deleteAriaLabel={deleteLabel}
          className={className}
          {...props}
        />
      );
    }

    // Render error state (extracted to FileCardError for max-lines compliance)
    if (variant === 'error') {
      return (
        <FileCardError
          ref={ref}
          fileName={fileName}
          fileType={fileType}
          errorMessage={errorMessage}
          failedLabel={failedLabel}
          fallbackErrorMessage={fallbackErrorMessage}
          size={size}
          onDelete={onDelete}
          deleteAriaLabel={deleteLabel}
          onRetry={onRetry}
          className={className}
          {...props}
        />
      );
    }

    // Get the display name - remove extension if it's a valid/known extension
    const displayFileName = shouldHideExtension
      ? fileName!.slice(0, fileName!.lastIndexOf('.'))
      : fileName;

    const fileNameElement = (
      <p
        ref={fileNameRef}
        className={cn(
          'min-w-0 self-stretch truncate font-medium text-text-primary',
          fileNameClasses
        )}
      >
        {displayFileName}
      </p>
    );

    return (
      <div
        ref={ref}
        className={cn(
          'group relative flex items-center border border-outline bg-surface shadow-sm transition-all',
          cardClasses,
          onClick && 'cursor-pointer hover:border-outline-hover',
          className
        )}
        {...interactiveProps}
        {...props}
      >
        {/* Thumbnail/Icon */}
        <div
          className={cn(
            'relative flex shrink-0 items-center justify-center overflow-hidden bg-background-tertiary',
            iconContainerClasses
          )}
        >
          {renderThumbnail()}
        </div>

        {/* File Info */}
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col overflow-hidden',
            textContainerClasses
          )}
        >
          {isFileNameTruncated ? (
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>{fileNameElement}</TooltipTrigger>
                <TooltipContent side="top" align="center">
                  {fileName}
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            fileNameElement
          )}
          <p className={cn('truncate font-normal text-text-secondary', fileTypeClasses)}>
            {displayFileType}
          </p>
        </div>

        {/* Delete Button */}
        {onDelete && <FileCardDeleteButton onDelete={onDelete} ariaLabel={deleteLabel} />}
      </div>
    );
  }
);

FileCard.displayName = 'FileCard';

export { FileCard };
